import React, { useState, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TextAlign from '@tiptap/extension-text-align';
import Color from '@tiptap/extension-color';
import TextStyle from '@tiptap/extension-text-style';
import Highlight from '@tiptap/extension-highlight';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { socialMediaAPI } from '@/lib/api';
import { toast } from 'sonner';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  Heading1, Heading2, Heading3,
  AlignLeft, AlignCenter, AlignRight,
  List, ListOrdered, Quote, Code,
  Link as LinkIcon, Image as ImageIcon,
  Table as TableIcon, Eye, Paintbrush,
} from 'lucide-react';

// ─── Custom extensions: preserve HTML email attributes ─────────────────────────
// These extend the base TipTap nodes to keep inline styles, bgcolor, width, etc.
// that are essential for HTML email rendering.

const CustomTable = Table.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: el => el.getAttribute('style'),
        renderHTML: attrs => attrs.style ? { style: attrs.style } : {},
      },
      border: {
        default: null,
        parseHTML: el => el.getAttribute('border'),
        renderHTML: attrs => (attrs.border !== null && attrs.border !== undefined) ? { border: attrs.border } : {},
      },
      cellpadding: {
        default: null,
        parseHTML: el => el.getAttribute('cellpadding'),
        renderHTML: attrs => (attrs.cellpadding !== null && attrs.cellpadding !== undefined) ? { cellpadding: attrs.cellpadding } : {},
      },
      cellspacing: {
        default: null,
        parseHTML: el => el.getAttribute('cellspacing'),
        renderHTML: attrs => (attrs.cellspacing !== null && attrs.cellspacing !== undefined) ? { cellspacing: attrs.cellspacing } : {},
      },
      width: {
        default: null,
        parseHTML: el => el.getAttribute('width'),
        renderHTML: attrs => attrs.width ? { width: attrs.width } : {},
      },
      align: {
        default: null,
        parseHTML: el => el.getAttribute('align'),
        renderHTML: attrs => attrs.align ? { align: attrs.align } : {},
      },
    };
  },
});

const CustomTableRow = TableRow.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: el => el.getAttribute('style'),
        renderHTML: attrs => attrs.style ? { style: attrs.style } : {},
      },
    };
  },
});

const CustomTableCell = TableCell.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: el => el.getAttribute('style'),
        renderHTML: attrs => attrs.style ? { style: attrs.style } : {},
      },
      bgcolor: {
        default: null,
        parseHTML: el => el.getAttribute('bgcolor'),
        renderHTML: attrs => attrs.bgcolor ? { bgcolor: attrs.bgcolor } : {},
      },
      width: {
        default: null,
        parseHTML: el => el.getAttribute('width'),
        renderHTML: attrs => attrs.width ? { width: attrs.width } : {},
      },
      height: {
        default: null,
        parseHTML: el => el.getAttribute('height'),
        renderHTML: attrs => attrs.height ? { height: attrs.height } : {},
      },
      align: {
        default: null,
        parseHTML: el => el.getAttribute('align'),
        renderHTML: attrs => attrs.align ? { align: attrs.align } : {},
      },
      valign: {
        default: null,
        parseHTML: el => el.getAttribute('valign'),
        renderHTML: attrs => attrs.valign ? { valign: attrs.valign } : {},
      },
    };
  },
});

const CustomTableHeader = TableHeader.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: el => el.getAttribute('style'),
        renderHTML: attrs => attrs.style ? { style: attrs.style } : {},
      },
      bgcolor: {
        default: null,
        parseHTML: el => el.getAttribute('bgcolor'),
        renderHTML: attrs => attrs.bgcolor ? { bgcolor: attrs.bgcolor } : {},
      },
      width: {
        default: null,
        parseHTML: el => el.getAttribute('width'),
        renderHTML: attrs => attrs.width ? { width: attrs.width } : {},
      },
      align: {
        default: null,
        parseHTML: el => el.getAttribute('align'),
        renderHTML: attrs => attrs.align ? { align: attrs.align } : {},
      },
      valign: {
        default: null,
        parseHTML: el => el.getAttribute('valign'),
        renderHTML: attrs => attrs.valign ? { valign: attrs.valign } : {},
      },
    };
  },
});

// Extends Image to preserve width, height, style, align — critical for email images
const CustomImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: el => el.getAttribute('width') || null,
        renderHTML: attrs => attrs.width ? { width: attrs.width } : {},
      },
      height: {
        default: null,
        parseHTML: el => el.getAttribute('height') || null,
        renderHTML: attrs => attrs.height ? { height: attrs.height } : {},
      },
      style: {
        default: null,
        parseHTML: el => el.getAttribute('style') || null,
        renderHTML: attrs => attrs.style ? { style: attrs.style } : {},
      },
      align: {
        default: null,
        parseHTML: el => el.getAttribute('align') || null,
        renderHTML: attrs => attrs.align ? { align: attrs.align } : {},
      },
    };
  },
});

// Strip dead blob: URLs from stored HTML.
// blob: URLs are session-only — they die on any page reload or new session.
// If one was saved to MongoDB it causes a broken image on every reopen.
const sanitizeBlobUrls = (html) => {
  if (!html) return html;
  return html.replace(/src="blob:[^"]*"/gi, 'src=""');
};

// ─── Variable chips ────────────────────────────────────────────────────────────
const VARIABLE_CHIPS = [
  { id: '{{first_name}}', label: 'First Name' },
  { id: '{{company}}', label: 'Company' },
  { id: '{{assigned_to_name}}', label: 'Assigned To' },
];

// ─── Toolbar button ────────────────────────────────────────────────────────────
const ToolBtn = ({ onClick, title, active, children }) => (
  <button
    type="button"
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    title={title}
    className={`p-1.5 rounded transition-colors
      ${active
        ? 'bg-[#1E2A33] text-white'
        : 'text-[#5C6670] hover:bg-[#F3EFE7] hover:text-[#1E2A33]'
      }`}
  >
    {children}
  </button>
);

const ToolDivider = () => (
  <div className="w-px h-5 bg-[#E3DDD1] mx-0.5 self-center" />
);

// ─── Main component ────────────────────────────────────────────────────────────
export function EmailEditor({ value = '', onChange, placeholder = 'Compose your email...' }) {
  const [activeTab, setActiveTab] = useState('visual');
  const [htmlSource, setHtmlSource] = useState(sanitizeBlobUrls(value) || '');
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [showTableMenu, setShowTableMenu] = useState(false);
  const [imageWidth, setImageWidth] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  const tableMenuRef = useRef(null);
  const imageInputRef = useRef(null);
  const colorInputRef = useRef(null);
  const lastValue = useRef(value);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      Link.configure({ openOnClick: false }),
      CustomImage.configure({ inline: false, allowBase64: false }),
      Placeholder.configure({ placeholder }),
      CustomTable.configure({ resizable: false }),
      CustomTableRow,
      CustomTableCell,
      CustomTableHeader,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Color,
      TextStyle,
      Highlight.configure({ multicolor: true }),
    ],
    content: sanitizeBlobUrls(value) || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      lastValue.current = html;
      setHtmlSource(html);
      onChange?.(html);
    },
    editorProps: {
      attributes: { class: 'tiptap-email-editor focus:outline-none' },
    },
  });

  // Track selected image width for the resize control
  useEffect(() => {
    if (!editor) return;
    const updateImageState = () => {
      if (editor.isActive('image')) {
        const attrs = editor.getAttributes('image');
        setImageWidth(attrs.width ? String(attrs.width) : '');
      } else {
        setImageWidth('');
      }
    };
    editor.on('selectionUpdate', updateImageState);
    editor.on('transaction', updateImageState);
    return () => {
      editor.off('selectionUpdate', updateImageState);
      editor.off('transaction', updateImageState);
    };
  }, [editor]);

  // Sync when parent resets value (switching sequences / opening a different record).
  // Also sanitizes any blob: URLs that were saved to MongoDB from a previous
  // session — those URLs are dead and cause broken images on reload.
  useEffect(() => {
    if (!editor) return;
    const clean = sanitizeBlobUrls(value) || '';
    if (clean !== lastValue.current) {
      lastValue.current = clean;
      setHtmlSource(clean);
      if (activeTab === 'visual') {
        editor.commands.setContent(clean, false);
      }
      // If a blob URL was found and removed, push the corrected HTML back to
      // the parent so it gets persisted to MongoDB on the next save.
      if (clean !== value) {
        onChange?.(clean);
      }
    }
  }, [value, editor, activeTab]);

  // Close table dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (tableMenuRef.current && !tableMenuRef.current.contains(e.target)) {
        setShowTableMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Tab switching ────────────────────────────────────────────────────────────
  const handleTabChange = (tab) => {
    if (tab === 'visual' && editor) {
      editor.commands.setContent(htmlSource || '', false);
    }
    setActiveTab(tab);
    setShowLinkInput(false);
    setShowTableMenu(false);
  };

  // ── HTML source edit ─────────────────────────────────────────────────────────
  const handleSourceChange = (e) => {
    const v = e.target.value;
    setHtmlSource(v);
    lastValue.current = v;
    onChange?.(v);
  };

  // ── Link ─────────────────────────────────────────────────────────────────────
  const handleInsertLink = () => {
    if (!editor || !linkUrl) return;
    const url = linkUrl.startsWith('http') ? linkUrl : `https://${linkUrl}`;
    editor.chain().focus().setLink({ href: url }).run();
    setLinkUrl('');
    setShowLinkInput(false);
  };

  // ── Image upload ─────────────────────────────────────────────────────────────
  // Upload to the backend first, then insert the image with the stable backend
  // URL. This avoids blob: URLs ever entering the editor or being saved to
  // MongoDB — blob URLs are session-only and break on any page reload.
  const handleImageFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editor) return;
    e.target.value = '';
    setImageUploading(true);
    try {
      const res = await socialMediaAPI.upload(file);
      const mediaId = res.data?.id;
      if (!mediaId) throw new Error('No media ID returned from upload');
      const backendUrl = socialMediaAPI.serveUrl(mediaId);
      editor.chain().focus().setImage({ src: backendUrl, alt: file.name }).run();
    } catch (err) {
      console.error('Image upload failed:', err);
      toast.error('Image upload failed');
    } finally {
      setImageUploading(false);
    }
  };

  // ── Apply image width ────────────────────────────────────────────────────────
  const applyImageWidth = (widthVal) => {
    if (!editor) return;
    const w = widthVal ? String(widthVal) : null;
    editor.chain().focus().updateAttributes('image', { width: w, height: null }).run();
  };

  // ── Apply image alignment ────────────────────────────────────────────────────
  // 'center' → block + auto margins (no text wrap)
  // 'left'   → float left, text wraps right
  // 'right'  → float right, text wraps left
  const applyImageAlign = (align) => {
    if (!editor) return;
    const styles = {
      center: 'display:block; margin:0 auto; float:none;',
      left:   'float:left; margin:0 16px 8px 0; display:block;',
      right:  'float:right; margin:0 0 8px 16px; display:block;',
    };
    editor.chain().focus().updateAttributes('image', { style: styles[align] }).run();
  };

  const imageStyle = editor?.getAttributes('image')?.style || '';
  const imageAlignCenter = imageStyle.includes('margin:0 auto') || imageStyle.includes('margin: 0 auto');
  const imageAlignLeft   = imageStyle.includes('float:left') || imageStyle.includes('float: left');
  const imageAlignRight  = imageStyle.includes('float:right') || imageStyle.includes('float: right');

  // ── Text colour ──────────────────────────────────────────────────────────────
  const handleColorChange = (e) => {
    editor?.chain().focus().setColor(e.target.value).run();
  };

  // ── Variable insertion ───────────────────────────────────────────────────────
  const handleInsertVariable = (varId) => {
    if (activeTab === 'visual' && editor) {
      editor.chain().focus().insertContent(varId).run();
    } else {
      setHtmlSource((prev) => {
        const v = prev + varId;
        onChange?.(v);
        return v;
      });
    }
  };

  const isInTable = editor?.isActive('table');
  const isImageActive = editor?.isActive('image');
  const currentColor = editor?.getAttributes('textStyle')?.color || '#1E2A33';

  return (
    <div className="border border-[#E3DDD1] rounded-lg overflow-visible bg-white">
      <Tabs value={activeTab} onValueChange={handleTabChange}>

        {/* ── Top bar ──────────────────────────────────────────────────────── */}
        <div className="border-b border-[#E3DDD1] bg-[#FBF8F2] rounded-t-lg">

          {/* Toolbar row */}
          <div className="flex items-center justify-between px-2 py-1 gap-1 flex-wrap">
            {activeTab === 'visual' && editor && (
              <div className="flex items-center gap-0.5 flex-wrap flex-1">

                {/* Text style */}
                <ToolBtn onClick={() => editor.chain().focus().toggleBold().run()} title="Bold" active={editor.isActive('bold')}>
                  <Bold className="w-4 h-4" />
                </ToolBtn>
                <ToolBtn onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic" active={editor.isActive('italic')}>
                  <Italic className="w-4 h-4" />
                </ToolBtn>
                <ToolBtn onClick={() => editor.chain().focus().toggleUnderline().run()} title="Underline" active={editor.isActive('underline')}>
                  <UnderlineIcon className="w-4 h-4" />
                </ToolBtn>
                <ToolBtn onClick={() => editor.chain().focus().toggleStrike().run()} title="Strikethrough" active={editor.isActive('strike')}>
                  <Strikethrough className="w-4 h-4" />
                </ToolBtn>

                <ToolDivider />

                {/* Headings */}
                <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="Heading 1" active={editor.isActive('heading', { level: 1 })}>
                  <Heading1 className="w-4 h-4" />
                </ToolBtn>
                <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Heading 2" active={editor.isActive('heading', { level: 2 })}>
                  <Heading2 className="w-4 h-4" />
                </ToolBtn>
                <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Heading 3" active={editor.isActive('heading', { level: 3 })}>
                  <Heading3 className="w-4 h-4" />
                </ToolBtn>

                <ToolDivider />

                {/* Alignment */}
                <ToolBtn onClick={() => editor.chain().focus().setTextAlign('left').run()} title="Align left" active={editor.isActive({ textAlign: 'left' })}>
                  <AlignLeft className="w-4 h-4" />
                </ToolBtn>
                <ToolBtn onClick={() => editor.chain().focus().setTextAlign('center').run()} title="Align center" active={editor.isActive({ textAlign: 'center' })}>
                  <AlignCenter className="w-4 h-4" />
                </ToolBtn>
                <ToolBtn onClick={() => editor.chain().focus().setTextAlign('right').run()} title="Align right" active={editor.isActive({ textAlign: 'right' })}>
                  <AlignRight className="w-4 h-4" />
                </ToolBtn>

                <ToolDivider />

                {/* Lists & blocks */}
                <ToolBtn onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet list" active={editor.isActive('bulletList')}>
                  <List className="w-4 h-4" />
                </ToolBtn>
                <ToolBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered list" active={editor.isActive('orderedList')}>
                  <ListOrdered className="w-4 h-4" />
                </ToolBtn>
                <ToolBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Blockquote" active={editor.isActive('blockquote')}>
                  <Quote className="w-4 h-4" />
                </ToolBtn>
                <ToolBtn onClick={() => editor.chain().focus().toggleCode().run()} title="Inline code" active={editor.isActive('code')}>
                  <Code className="w-4 h-4" />
                </ToolBtn>

                <ToolDivider />

                {/* Link */}
                <ToolBtn
                  onClick={() => {
                    if (editor.isActive('link')) {
                      editor.chain().focus().unsetLink().run();
                    } else {
                      setShowLinkInput((s) => !s);
                    }
                  }}
                  title={editor.isActive('link') ? 'Remove link' : 'Insert link'}
                  active={editor.isActive('link') || showLinkInput}
                >
                  <LinkIcon className="w-4 h-4" />
                </ToolBtn>

                {/* Image */}
                <ToolBtn
                  onClick={() => !imageUploading && imageInputRef.current?.click()}
                  title={imageUploading ? 'Uploading…' : 'Insert image'}
                >
                  {imageUploading
                    ? <svg className="w-4 h-4 animate-spin text-[#A62B1F]" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                      </svg>
                    : <ImageIcon className="w-4 h-4" />
                  }
                </ToolBtn>
                <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageFile} />

                {/* Image controls — shown when an image is selected */}
                {isImageActive && (
                  <>
                    <ToolDivider />
                    <div className="flex items-center gap-1" title="Image width in pixels">
                      <ImageIcon className="w-3 h-3 text-[#5C6670]" />
                      <input
                        type="number"
                        min="10"
                        max="1200"
                        step="10"
                        className="w-16 text-xs border border-[#E3DDD1] rounded px-1.5 py-0.5 text-center focus:outline-none focus:border-[#A62B1F]"
                        placeholder="width"
                        value={imageWidth}
                        onChange={(e) => setImageWidth(e.target.value)}
                        onBlur={(e) => applyImageWidth(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.currentTarget.blur(); } }}
                      />
                      <span className="text-xs text-[#9AA3AB]">px</span>
                    </div>
                    <ToolDivider />
                    <ToolBtn onClick={() => applyImageAlign('left')}  title="Float left (text wraps right)" active={imageAlignLeft}>
                      <AlignLeft className="w-4 h-4" />
                    </ToolBtn>
                    <ToolBtn onClick={() => applyImageAlign('center')} title="Center image" active={imageAlignCenter}>
                      <AlignCenter className="w-4 h-4" />
                    </ToolBtn>
                    <ToolBtn onClick={() => applyImageAlign('right')} title="Float right (text wraps left)" active={imageAlignRight}>
                      <AlignRight className="w-4 h-4" />
                    </ToolBtn>
                  </>
                )}

                {/* Table */}
                <div className="relative" ref={tableMenuRef}>
                  <ToolBtn
                    onClick={() => setShowTableMenu((s) => !s)}
                    title="Table"
                    active={isInTable || showTableMenu}
                  >
                    <TableIcon className="w-4 h-4" />
                  </ToolBtn>
                  {showTableMenu && (
                    <div className="absolute top-full left-0 mt-1 bg-white border border-[#E3DDD1] rounded-lg shadow-xl z-[100] p-2 min-w-[190px]">
                      <p className="text-xs text-[#9AA3AB] px-2 pb-1 font-medium uppercase tracking-wide">Insert Table</p>
                      {[['2 × 2', 2, 2], ['3 × 3', 3, 3], ['3 × 4', 3, 4], ['4 × 4', 4, 4], ['4 × 5', 4, 5]].map(([label, rows, cols]) => (
                        <button
                          key={label}
                          type="button"
                          className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-[#F3EFE7] text-[#1E2A33]"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
                            setShowTableMenu(false);
                          }}
                        >
                          {label} table
                        </button>
                      ))}
                      {isInTable && (
                        <>
                          <div className="border-t border-[#E3DDD1] my-1.5" />
                          <p className="text-xs text-[#9AA3AB] px-2 pb-1 font-medium uppercase tracking-wide">Edit Table</p>
                          {[
                            ['Add row above', () => editor.chain().focus().addRowBefore().run()],
                            ['Add row below', () => editor.chain().focus().addRowAfter().run()],
                            ['Delete row', () => editor.chain().focus().deleteRow().run()],
                            ['Add column before', () => editor.chain().focus().addColumnBefore().run()],
                            ['Add column after', () => editor.chain().focus().addColumnAfter().run()],
                            ['Delete column', () => editor.chain().focus().deleteColumn().run()],
                            ['Delete table', () => editor.chain().focus().deleteTable().run()],
                          ].map(([label, action]) => (
                            <button
                              key={label}
                              type="button"
                              className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-[#F3EFE7] text-[#1E2A33]"
                              onMouseDown={(e) => { e.preventDefault(); action(); setShowTableMenu(false); }}
                            >
                              {label}
                            </button>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>

                <ToolDivider />

                {/* Text colour */}
                <div className="relative" title="Text colour">
                  <button
                    type="button"
                    className="p-1.5 rounded hover:bg-[#F3EFE7] transition-colors flex flex-col items-center gap-0"
                    onMouseDown={(e) => { e.preventDefault(); colorInputRef.current?.click(); }}
                    title="Text colour"
                  >
                    <span className="text-sm font-bold text-[#5C6670] leading-none">A</span>
                    <span className="w-4 h-1 rounded-sm mt-0.5" style={{ backgroundColor: currentColor }} />
                  </button>
                  <input
                    ref={colorInputRef}
                    type="color"
                    defaultValue="#1E2A33"
                    className="absolute inset-0 opacity-0 w-full h-full cursor-pointer pointer-events-none"
                    onChange={handleColorChange}
                  />
                </div>

                {/* Clear formatting */}
                <ToolBtn onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()} title="Clear formatting">
                  <Paintbrush className="w-4 h-4" />
                </ToolBtn>

              </div>
            )}

            {activeTab !== 'visual' && <div className="flex-1" />}

            {/* Tab switcher */}
            <TabsList className="h-7 bg-white border border-[#E3DDD1] rounded-md p-0.5 shrink-0">
              <TabsTrigger value="visual" className="h-6 text-xs px-2 data-[state=active]:bg-[#1E2A33] data-[state=active]:text-white rounded">
                Visual
              </TabsTrigger>
              <TabsTrigger value="html" className="h-6 text-xs px-2 data-[state=active]:bg-[#1E2A33] data-[state=active]:text-white rounded">
                HTML
              </TabsTrigger>
              <TabsTrigger value="preview" className="h-6 text-xs px-2 data-[state=active]:bg-[#1E2A33] data-[state=active]:text-white rounded">
                <Eye className="w-3 h-3 mr-1" />Preview
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Link input row */}
          {showLinkInput && activeTab === 'visual' && (
            <div className="flex items-center gap-2 px-3 py-2 border-t border-[#E3DDD1]">
              <input
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleInsertLink()}
                placeholder="https://example.com"
                className="flex-1 text-sm border border-[#E3DDD1] rounded px-2 py-1 outline-none focus:border-[#A62B1F]"
                autoFocus
              />
              <Button size="sm" onClick={handleInsertLink} className="h-7 text-xs bg-[#1E2A33] text-white">Insert</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowLinkInput(false)} className="h-7 text-xs">Cancel</Button>
            </div>
          )}
        </div>

        {/* ── VISUAL tab ───────────────────────────────────────────────────── */}
        <TabsContent value="visual" className="m-0">
          <div className="min-h-[200px] max-h-[500px] overflow-y-auto px-4 py-3">
            <EditorContent editor={editor} />
          </div>
        </TabsContent>

        {/* ── HTML tab ─────────────────────────────────────────────────────── */}
        <TabsContent value="html" className="m-0">
          <Textarea
            value={htmlSource}
            onChange={handleSourceChange}
            className="min-h-[200px] rounded-none border-0 font-mono text-xs resize-y focus-visible:ring-0"
            placeholder="<p>Your HTML here...</p>"
            spellCheck={false}
          />
        </TabsContent>

        {/* ── PREVIEW tab ──────────────────────────────────────────────────── */}
        <TabsContent value="preview" className="m-0">
          <div
            className="min-h-[200px] max-h-[500px] overflow-y-auto px-4 py-3 text-sm text-[#1E2A33] tiptap-preview"
            style={{ lineHeight: '1.6', fontFamily: 'sans-serif' }}
          >
            {htmlSource ? (
              <div
                dangerouslySetInnerHTML={{
                  __html: htmlSource
                    .replace(/\{\{first_name\}\}/g, '<span style="background:#FFF3CD;padding:0 3px;border-radius:3px;font-weight:600">John</span>')
                    .replace(/\{\{company\}\}/g, '<span style="background:#FFF3CD;padding:0 3px;border-radius:3px;font-weight:600">Acme Corp</span>')
                    .replace(/\{\{assigned_to_name\}\}/g, '<span style="background:#FFF3CD;padding:0 3px;border-radius:3px;font-weight:600">Jane Smith</span>'),
                }}
              />
            ) : (
              <p className="text-[#9AA3AB] italic">Nothing to preview yet.</p>
            )}
          </div>
        </TabsContent>

      </Tabs>

      {/* ── Variable chips ────────────────────────────────────────────────────── */}
      {activeTab !== 'preview' && (
        <div className="flex items-center gap-2 px-3 py-2 border-t border-[#E3DDD1] bg-[#FBF8F2] flex-wrap">
          <span className="text-xs text-[#9AA3AB] mr-1">Insert:</span>
          {VARIABLE_CHIPS.map((chip) => (
            <button
              key={chip.id}
              type="button"
              onClick={() => handleInsertVariable(chip.id)}
              className="text-xs px-2 py-0.5 rounded-full border border-[#A62B1F] text-[#A62B1F] hover:bg-[#A62B1F] hover:text-white transition-colors font-mono"
            >
              {chip.id}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
